import { DOCUMENT_EXTENSION_GROUPS } from '../constants/fileTypes'

const wordExtensions = DOCUMENT_EXTENSION_GROUPS.WORD
const excelExtensions = DOCUMENT_EXTENSION_GROUPS.EXCEL
const powerPointExtensions = DOCUMENT_EXTENSION_GROUPS.POWERPOINT

const getFileExtension = (value) => {
  const source = String(value || '').toLowerCase()
  const cleanValue = source.split('?')[0].split('#')[0]
  const segments = cleanValue.split('.')
  return segments.length > 1 ? segments.pop() : ''
}

export const isBrandableContentLibraryItem = (item) => {
  const brandingEngine = item?.brandingEngine
  return typeof brandingEngine === 'string' && brandingEngine.trim().length > 0
}

// Zoom / external video items use "Go to Video Page" in ContentLibraryItemCard (zoomUrl).
// They must not receive document download actions here.
export const hasDirectDocumentDownload = (item) =>
  Boolean(item?.documentFileName) && !isBrandableContentLibraryItem(item)

const getItemFileExtension = (item) => getFileExtension(item?.documentFileName)

const getExtensionDownloadLabel = (fileExtension) => {
  if (fileExtension === 'pdf') {
    return 'Download PDF File'
  }

  if (wordExtensions.includes(fileExtension)) {
    return 'Download Word File'
  }

  if (excelExtensions.includes(fileExtension)) {
    return 'Download Excel File'
  }

  if (powerPointExtensions.includes(fileExtension)) {
    return 'Download PowerPoint File'
  }

  return 'Download File'
}

const getExtensionDownloadIcon = (fileExtension) => {
  if (fileExtension === 'pdf') {
    return 'bi-filetype-pdf'
  }

  if (wordExtensions.includes(fileExtension)) {
    return 'bi-file-earmark-word'
  }

  if (excelExtensions.includes(fileExtension)) {
    return 'bi-filetype-xls'
  }

  if (powerPointExtensions.includes(fileExtension)) {
    return 'bi-file-earmark-ppt-fill'
  }

  return 'bi-download'
}

const createDocumentAction = ({ key, label, icon, format = null, isBrandable = false }) => ({
  key,
  label,
  icon,
  format,
  isBrandable
})

const getDirectDownloadActions = (item) => {
  const fileExtension = getItemFileExtension(item)

  return [
    createDocumentAction({
      key: 'document-download',
      label: getExtensionDownloadLabel(fileExtension),
      icon: getExtensionDownloadIcon(fileExtension),
      isBrandable: false
    })
  ]
}

const getBrandableDocumentActions = (item) => {
  const fileExtension = getItemFileExtension(item)

  if (wordExtensions.includes(fileExtension)) {
    return [
      createDocumentAction({
        key: 'branding-pdf-download',
        label: 'Download PDF File',
        icon: 'bi-filetype-pdf',
        format: 'pdf',
        isBrandable: true
      }),
      createDocumentAction({
        key: 'branding-word-download',
        label: 'Download Word File',
        icon: 'bi-file-earmark-word',
        format: 'docx',
        isBrandable: true
      })
    ]
  }

  if (excelExtensions.includes(fileExtension)) {
    return [
      createDocumentAction({
        key: 'branding-excel-download',
        label: 'Download Excel File',
        icon: 'bi-filetype-xls',
        format: 'xlsx',
        isBrandable: true
      })
    ]
  }

  if (powerPointExtensions.includes(fileExtension)) {
    return [
      createDocumentAction({
        key: 'branding-powerpoint-download',
        label: 'Download PowerPoint File',
        icon: 'bi-file-earmark-ppt-fill',
        format: 'pptx',
        isBrandable: true
      })
    ]
  }

  return [
    createDocumentAction({
      key: 'branding-pdf-download',
      label: 'Download PDF File',
      icon: 'bi-filetype-pdf',
      format: 'pdf',
      isBrandable: true
    })
  ]
}

export const useContentLibraryDocumentDownloadActions = () => {
  const getDocumentActions = (item) => {
    if (!hasDirectDocumentDownload(item) && !isBrandableContentLibraryItem(item)) {
      return []
    }

    if (isBrandableContentLibraryItem(item)) {
      return getBrandableDocumentActions(item)
    }

    return getDirectDownloadActions(item)
  }

  const getDocumentActionLabel = (item) => {
    const fileExtension = getItemFileExtension(item)
    return getExtensionDownloadLabel(fileExtension)
  }

  const getDocumentActionIcon = (item) => {
    const fileExtension = getItemFileExtension(item)
    return getExtensionDownloadIcon(fileExtension)
  }

  return {
    getDocumentActions,
    getDocumentActionLabel,
    getDocumentActionIcon,
    isBrandableContentLibraryItem,
    hasDirectDocumentDownload
  }
}
